import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Claims Denial Prevention Page
 * Features current industry statistics and Videxa's solutions
 */
const ClaimsDenialPage = () => {
  const industryStats = [
    {
      stat: "11.8%",
      description: "Initial claim denial rate in 2024",
      source: "Kodiak Solutions",
      sourceUrl: "https://www.businesswire.com/news/home/20250521892947/en/Rate-of-initial-denials-of-medical-insurance-claims-continued-to-rise-in-2024-Kodiak-Solutions-proprietary-data-show"
    },
    {
      stat: "$19.7B",
      description: "Annual cost to fight denied claims",
      source: "AHA / Premier",
      sourceUrl: "https://www.aha.org/aha-center-health-innovation-market-scan/2024-04-02-payer-denial-tactics-how-confront-20-billion-problem"
    },
    {
      stat: "41%",
      description: "Providers with >10% denial rates",
      source: "Experian State of Claims 2025",
      sourceUrl: "https://www.experian.com/blogs/healthcare/state-of-claims-2025/"
    },
    {
      stat: "54%",
      description: "Denied claims ultimately overturned",
      source: "AHA Research",
      sourceUrl: "https://www.aha.org/aha-center-health-innovation-market-scan/2024-04-02-payer-denial-tactics-how-confront-20-billion-problem"
    }
  ];

  const denialCauses = [
    {
      percentage: "50%",
      cause: "Missing or Inaccurate Claims Data",
      description: "Incomplete patient information, coding errors, and missing documentation are the leading causes of claim denials.",
      source: "Experian State of Claims 2025",
      sourceUrl: "https://www.experian.com/blogs/healthcare/healthcare-claim-denials-statistics-state-of-claims-report/"
    },
    {
      percentage: "35%",
      cause: "Prior Authorization Issues",
      description: "Authorization problems continue to be a top operational challenge, with physicians spending 13 hours per week on prior authorizations.",
      source: "AMA Survey 2024",
      sourceUrl: "https://www.medicaleconomics.com/view/2025-state-of-claims-why-are-denials-increasing-"
    },
    {
      percentage: "77%",
      cause: "Paperwork or Plan Design",
      description: "Three in four denials stem from administrative issues, not medical judgment—indicating preventable causes.",
      source: "Industry Analysis",
      sourceUrl: "https://www.experian.com/blogs/healthcare/understanding-healthcare-claim-denials-reasons-and-solutions/"
    }
  ];

  const payerDenialRates = [
    { payer: "Medicaid", rate: "16.7%", color: "red" },
    { payer: "Medicare Advantage", rate: "15.7%", color: "orange" },
    { payer: "Managed Medicaid", rate: "15.1%", color: "yellow" },
    { payer: "Private Payers", rate: "15.0%", color: "blue" },
    { payer: "Commercial", rate: "13.9%", color: "green" },
    { payer: "Traditional Medicare", rate: "8.4%", color: "teal" }
  ];

  const videxaSolutions = [
    {
      title: "AI-Powered Claim Scrubbing",
      description: "Real-time review of clinical documentation to flag missing elements, suggest proper coding, and ensure prior authorization requirements are met before submission.",
      icon: "🤖"
    },
    {
      title: "Predictive Denial Analytics",
      description: "Machine learning models identify which claims are most likely to be denied and why, allowing proactive intervention before submission.",
      icon: "📊"
    },
    {
      title: "Automated Eligibility Verification",
      description: "Real-time patient eligibility checks and benefits verification at the point of registration to eliminate preventable denials.",
      icon: "✓"
    },
    {
      title: "Prior Authorization Workflow",
      description: "Centralized, automated prior authorization management that reduces physician burden and ensures timely approvals.",
      icon: "📋"
    },
    {
      title: "Denial Management Dashboard",
      description: "Real-time monitoring of denial rates, root causes, and trends with automated alerts for immediate action.",
      icon: "📈"
    },
    {
      title: "Appeals Automation",
      description: "Streamlined appeals workflow with templated responses and documentation management to maximize overturn rates.",
      icon: "⚡"
    }
  ];

  return (
    <div className="claims-denial-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Claims Denial <span style={{ color: '#FFC000' }}>Prevention</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Stop revenue leakage before it starts with AI-powered denial prevention
            </p>
            <p className="text-lg mb-8 text-gray-600">
              Healthcare providers lose billions annually to preventable claim denials. Videxa's intelligent platform helps you submit clean claims the first time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Statistics Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">
            The State of Claims Denials in 2024-2025
          </h2>
          <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto">
            Denial rates continue to climb, creating significant financial and operational burdens for healthcare providers.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {industryStats.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FFC000] mb-2">{item.stat}</div>
                <p className="text-gray-300 mb-2">{item.description}</p>
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 underline"
                >
                  Source: {item.source}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Causes Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Top Causes of Claims Denials
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Understanding why claims are denied is the first step to preventing them.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {denialCauses.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg shadow-lg"
              >
                <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">
                  {item.percentage}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                  {item.cause}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {item.description}
                </p>
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Source: {item.source}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Denial Rates by Payer */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Denial Rates by Payer Type
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Initial denial rates vary significantly across payer types, with government programs showing both the highest and lowest rates.
            <a
              href="https://www.techtarget.com/revcyclemanagement/feature/Breaking-down-claim-denial-rates-by-healthcare-payer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline ml-1"
            >
              (Source: TechTarget)
            </a>
          </p>

          <div className="max-w-3xl mx-auto">
            {payerDenialRates.map((payer, index) => (
              <div key={index} className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{payer.payer}</span>
                  <span className="text-gray-900 dark:text-white font-bold">{payer.rate}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div
                    className="bg-[#0E3056] dark:bg-[#FFC000] h-4 rounded-full transition-all duration-500"
                    style={{ width: payer.rate }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Hidden Cost */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">The Hidden Cost of Denials</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white/10 p-6 rounded-lg">
                <div className="text-3xl font-bold text-[#FFC000] mb-2">69%</div>
                <p>of AI users report reduced denials and increased resubmission success</p>
                <a
                  href="https://www.experian.com/blogs/healthcare/state-of-claims-2025/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-300 hover:underline mt-2 inline-block"
                >
                  Source: Experian
                </a>
              </div>
              <div className="bg-white/10 p-6 rounded-lg">
                <div className="text-3xl font-bold text-[#FFC000] mb-2">14%</div>
                <p>of providers currently use AI to reduce denials—a massive opportunity gap</p>
                <a
                  href="https://www.experian.com/blogs/healthcare/healthcare-claim-denials-statistics-state-of-claims-report/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-300 hover:underline mt-2 inline-block"
                >
                  Source: Experian
                </a>
              </div>
            </div>
            <p className="text-xl text-gray-200">
              Payers appear to use initial denials to slow payments, even though they ultimately pay approximately 90% of claims.
              <a
                href="https://www.aha.org/aha-center-health-innovation-market-scan/2024-04-02-payer-denial-tactics-how-confront-20-billion-problem"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:underline ml-1"
              >
                (AHA)
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Videxa Solutions */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            How Videxa Prevents Denials
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Our AI-powered platform addresses the root causes of denials before claims are submitted.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {videxaSolutions.map((solution, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{solution.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                  {solution.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Results You Can Expect
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">65%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduction in Denial Rates</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">94%</div>
              <p className="text-gray-600 dark:text-gray-300">First-Pass Acceptance</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">40%</div>
              <p className="text-gray-600 dark:text-gray-300">Faster Reimbursement</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">3x</div>
              <p className="text-gray-600 dark:text-gray-300">ROI in First Year</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to reduce your denial rate?"
        description="Join healthcare organizations that have cut denials by 65% with Videxa's AI-powered platform."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Contact Sales"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default ClaimsDenialPage;
